import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import Fade from "@mui/material/Fade"

import Icon from "@/components/icon/Icon"
import { colorTokens } from "@/components/colorTokens"
import BasicButton from "@/components/button/BasicButton"
import { DialogProps } from "@/types/components/dialog"
import { createDialogStore } from "@/stores/dialog"

import "./dialog.scss"

export default function Dialog({
  open,
  title,
  children,
  onClose,
  onConfirm,
  cancelTextKey,
  confirmTextKey,
  footer,
  noTitleButton,
  maxWidth,
  maxHeight,
  className,
}: DialogProps) {
  const { t } = useTranslation("")
  const { lastOpenedDialogId, setLastOpenedDialogId } = createDialogStore()

  const dialogId = useRef(`dialog-${Math.random()}`).current
  const [actualRender, setActualRender] = useState(false)
  const [isEscPressed, setIsEscPressed] = useState(false)
  const [bodyHeight, setBodyHeight] = useState(0)

  useEffect(() => {
    const onPressEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && lastOpenedDialogId === dialogId) {
        setIsEscPressed(true)
      }
    }

    document.addEventListener("keydown", onPressEsc)

    return () => {
      document.removeEventListener("keydown", onPressEsc)
    }
  }, [lastOpenedDialogId, dialogId])

  // 바인딩으로 인한 버그 수정
  useEffect(() => {
    if (isEscPressed) {
      onClose()
      setIsEscPressed(false)
    }
  }, [isEscPressed])

  useEffect(() => {
    if (open) {
      setActualRender(true)
      document.body.style.overflow = "hidden"
      if (lastOpenedDialogId === null) {
        setLastOpenedDialogId(dialogId)
      }
    } else {
      setLastOpenedDialogId(null)
      document.body.style.overflow = ""
      setTimeout(() => {
        setActualRender(false)
      }, 300)
    }

    return () => {
      document.body.style.overflow = ""
    }
  }, [open, dialogId])

  useEffect(() => {
    setBodyHeight(document.body.clientHeight)
  }, [open])

  if (!actualRender) {
    return null
  }

  return (
    <>
      <Fade in={open}>
        <div
          className={`DialogBg ${className ?? ""}`}
          onClick={onClose}
          style={{
            height: bodyHeight,
          }}
        />
      </Fade>
      <Fade in={open}>
        <div
          className="DialogCont"
          style={{
            maxWidth,
            maxHeight,
          }}>
          <div className="dialog-header">
            <h1>{title}</h1>
            {!noTitleButton && (
              <button onClick={onClose}>
                <Icon type="close" size={24} color={colorTokens.icon.default} />
              </button>
            )}
          </div>
          <div className="dialog-body">{children}</div>
          <div className="dialog-footer">
            {footer ?? (
              <>
                <BasicButton type="default-outlined" size="md" onClick={onClose}>
                  <p>{t(cancelTextKey ?? "button.cancel")}</p>
                </BasicButton>
                <BasicButton type="primary-contained" size="md" onClick={onConfirm}>
                  {t(confirmTextKey ?? "button.save")}
                </BasicButton>
              </>
            )}
          </div>
        </div>
      </Fade>
    </>
  )
}
